import { useCallback, useEffect, useRef } from 'react';
import { getGroupSettingsFunc, updateGroupSettingsFunc } from '../../../lib/helper';
import { CustomField, GroupSettings, TagGroup } from '../../../types/common';
import { DashboardColumn, InvestmentThesis, Workspace } from '../../../types/files';
import ObjectUtils from '../../../utils/ObjectUtils';
import useGroupSettingsContext from '../useGroupSettingsContext';

const useGroupSettingsEffects = (userGroup: string | null) => {
  const {
    settingsLoaded,
    group,
    defaultStatus,
    statuses,
    tags,
    tagGroups,
    keyPeopleTags,
    customFields,
    stages,
    externalSync,
    autoIngestFromEmail,
    autoSendDigestEmail,
    autoSendIngestionNotifications,
    investmentThesis,
    groupWorkspaces,
    excludedCompanyTerms,
    dashboardColumns,
    setGroupSettingsLoaded,
    setGroupSettings
  } = useGroupSettingsContext();

  const updatedDefaultStatusRef = useRef<string>('');
  const updatedStatusesRef = useRef<string[]>([]);
  const updatedTagsRef = useRef<string[]>([]);
  const updatedKeyPeopleTagsRef = useRef<string[]>([]);
  const updatedTagGroupsRef = useRef<TagGroup[]>([]);
  const updatedCustomFieldsRef = useRef<CustomField[]>([]);
  const updatedStagesRef = useRef<string[]>([]);
  const updatedAutoSyncRef = useRef<boolean>(false);
  const updatedAutoIngestRef = useRef<boolean>(false);
  const updatedAutoSendDigestRef = useRef<boolean>(false);
  const updatedAutoSendIngestNotifRef = useRef<boolean>(false);
  const updatedInvestmentThesis = useRef<InvestmentThesis|null>(null);
  const updatedGroupWorkspacesRef = useRef<Workspace[]>([]);
  const updatedExcludedCompanyTermsRef = useRef<string[]>([]);
  const updatedDashboardColumnsRef = useRef<DashboardColumn[]>([]);

  const refreshSettings = useCallback(() => {
    getGroupSettingsFunc({}).then((groupSettingsData?: GroupSettings) => {
      updatedDefaultStatusRef.current = groupSettingsData?.defaultStatus || '';
      updatedTagsRef.current = [...groupSettingsData?.tags ?? []].sort((a, b) => a > b ? 1 : 0) ?? [];
      updatedKeyPeopleTagsRef.current = [...groupSettingsData?.keyPeopleTags ?? []].sort((a, b) => a > b ? 1 : 0) ?? [];
      updatedTagGroupsRef.current = groupSettingsData?.tagGroups ?? [];
      updatedCustomFieldsRef.current = groupSettingsData?.customFields ?? [];
      updatedStagesRef.current = [...groupSettingsData?.investmentStages ?? []].sort((a, b) => a > b ? 1 : 0) ?? [];
      updatedStatusesRef.current = groupSettingsData?.statuses?.map(statuses => !!statuses ? statuses.trim().toLowerCase() : '<rename this statuses>') ?? [];
      updatedAutoSyncRef.current = groupSettingsData?.shouldSyncAllDashboardsExternally ?? false;
      updatedAutoIngestRef.current = groupSettingsData?.shouldIngestDashboardsFromEmail ?? false;
      updatedAutoSendDigestRef.current = groupSettingsData?.shouldSendDigestEmail ?? false;
      updatedAutoSendIngestNotifRef.current = groupSettingsData?.shouldSendIngestionNotifications ?? false;
      updatedInvestmentThesis.current = groupSettingsData?.investmentThesis ?? null;
      updatedGroupWorkspacesRef.current = groupSettingsData?.workspaces ?? [];
      updatedExcludedCompanyTermsRef.current = groupSettingsData?.excludedCompanyTerms ?? [];
      updatedDashboardColumnsRef.current = groupSettingsData?.dashboardColumns ?? [];

      setGroupSettings(groupSettingsData!);
      setGroupSettingsLoaded(true);
    });
  }, [setGroupSettings, setGroupSettingsLoaded]);

  useEffect(() => {
    if (userGroup) {
      setGroupSettingsLoaded(false);
      refreshSettings();
    }
  // eslint-disable-next-line
  }, [userGroup]);

  useEffect(() => {
    if (settingsLoaded) {
      if (!ObjectUtils.equalObjects(defaultStatus, updatedDefaultStatusRef.current)) {
        Promise.resolve(updateGroupSettingsFunc({ group, defaultStatus }));
        updatedDefaultStatusRef.current = structuredClone(defaultStatus || '');
      }
    }
  // eslint-disable-next-line
  }, [defaultStatus]);

  useEffect(() => {
    if (settingsLoaded) {
      if (!ObjectUtils.equalObjects(statuses, updatedStatusesRef.current)) {
        Promise.resolve(updateGroupSettingsFunc({ group, statuses }));
        updatedStatusesRef.current = structuredClone(statuses ?? []);
      }
    }
  // eslint-disable-next-line
  }, [statuses]);

  useEffect(() => {
    if (settingsLoaded) {
      if (!ObjectUtils.equalObjects(tags, updatedTagsRef.current)) {
        Promise.resolve(updateGroupSettingsFunc({ group, tags }));
        updatedTagsRef.current = structuredClone(tags ?? []);
      }
    }
  // eslint-disable-next-line
  }, [tags]);

  useEffect(() => {
    if (settingsLoaded) {
      if (!ObjectUtils.equalObjects(keyPeopleTags, updatedKeyPeopleTagsRef.current)) {
        Promise.resolve(updateGroupSettingsFunc({ group, keyPeopleTags }));
        updatedKeyPeopleTagsRef.current = structuredClone(keyPeopleTags ?? []);
      }
    }
  // eslint-disable-next-line
  }, [keyPeopleTags]);

  useEffect(() => {
    if (settingsLoaded) {
      if (!ObjectUtils.equalObjects(tagGroups, updatedTagGroupsRef.current)) {
        Promise.resolve(updateGroupSettingsFunc({ group, tagGroups }));
        updatedTagGroupsRef.current = structuredClone(tagGroups ?? []);
      }
    }
  // eslint-disable-next-line
  }, [tagGroups]);

  useEffect(() => {
    if (settingsLoaded) {
      if (!ObjectUtils.equalObjects(customFields, updatedCustomFieldsRef.current)) {
        Promise.resolve(updateGroupSettingsFunc({ group, customFields }));
        updatedCustomFieldsRef.current = structuredClone(customFields ?? []);
      }
    }
  // eslint-disable-next-line
  }, [customFields]);

  useEffect(() => {
    if (settingsLoaded) {
      if (!ObjectUtils.equalObjects(stages, updatedStagesRef.current)) {
        Promise.resolve(updateGroupSettingsFunc({ group, investmentStages: stages, }));
        updatedStagesRef.current = structuredClone(stages || []);
      }
    }
  // eslint-disable-next-line
  }, [stages]);

  useEffect(() => {
    if (settingsLoaded) {
      if (!ObjectUtils.equalObjects(externalSync, updatedAutoSyncRef.current)) {
        Promise.resolve(updateGroupSettingsFunc({ group, shouldSyncAllDashboardsExternally: externalSync, }));
        updatedAutoSyncRef.current = structuredClone(externalSync || false);
      }
    }
  // eslint-disable-next-line
  }, [externalSync]);

  useEffect(() => {
    if (settingsLoaded) {
      if (!ObjectUtils.equalObjects(autoIngestFromEmail, updatedAutoIngestRef.current)) {
        Promise.resolve(updateGroupSettingsFunc({ group, shouldIngestDashboardsFromEmail: autoIngestFromEmail, }));
        updatedAutoIngestRef.current = structuredClone(autoIngestFromEmail || false);
      }
    }
  // eslint-disable-next-line
  }, [autoIngestFromEmail]);

  useEffect(() => {
    if (settingsLoaded) {
      if (!ObjectUtils.equalObjects(autoSendDigestEmail, updatedAutoSendDigestRef.current)) {
        Promise.resolve(updateGroupSettingsFunc({ group, shouldSendDigestEmail: autoSendDigestEmail, }));
        updatedAutoSendDigestRef.current = structuredClone(autoSendDigestEmail || false);
      }
    }
  // eslint-disable-next-line
  }, [autoSendDigestEmail]);

  useEffect(() => {
    if (settingsLoaded) {
      if (!ObjectUtils.equalObjects(autoSendIngestionNotifications, updatedAutoSendIngestNotifRef.current)) {
        Promise.resolve(updateGroupSettingsFunc({ group, shouldSendIngestionNotifications: autoSendIngestionNotifications, }));
        updatedAutoSendIngestNotifRef.current = structuredClone(autoSendIngestionNotifications || false);
      }
    }
  // eslint-disable-next-line
  }, [autoSendIngestionNotifications]);

  useEffect(() => {
    if (settingsLoaded) {
      if (!ObjectUtils.equalObjects(investmentThesis, updatedInvestmentThesis.current)) {
        Promise.resolve(updateGroupSettingsFunc({ group, investmentThesis: investmentThesis!, }));
        updatedInvestmentThesis.current = structuredClone(investmentThesis ?? null);
      }
    }
  // eslint-disable-next-line
  }, [investmentThesis]);

  useEffect(() => {
    if (settingsLoaded) {
      if (!ObjectUtils.equalObjects(groupWorkspaces, updatedGroupWorkspacesRef.current)) {
        Promise.resolve(updateGroupSettingsFunc({ group, workspaces: groupWorkspaces, }));
        updatedGroupWorkspacesRef.current = structuredClone(groupWorkspaces || []);
      }
    }
  // eslint-disable-next-line
  }, [groupWorkspaces]);

  useEffect(() => {
    if (settingsLoaded) {
      if (!ObjectUtils.equalObjects(excludedCompanyTerms, updatedExcludedCompanyTermsRef.current)) {
        Promise.resolve(updateGroupSettingsFunc({ group, excludedCompanyTerms: excludedCompanyTerms, }));
        updatedExcludedCompanyTermsRef.current = structuredClone(excludedCompanyTerms ?? []);
      }
    }
  // eslint-disable-next-line
  }, [excludedCompanyTerms]);

  useEffect(() => {
    if (settingsLoaded) {
      if (!ObjectUtils.equalObjects(JSON.stringify(dashboardColumns), JSON.stringify(updatedDashboardColumnsRef.current))) {
        Promise.resolve(updateGroupSettingsFunc({ group, dashboardColumns: dashboardColumns, }));
        updatedDashboardColumnsRef.current = structuredClone(dashboardColumns ?? []);
      }
    }
  // eslint-disable-next-line
  }, [dashboardColumns]);

  return { refreshSettings };
};

export default useGroupSettingsEffects;